import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Upload, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Bulk user upload with a pre-commit duplicate review (Skip / Replace / Add anyway).
export function UsersUploadDialog({ open, onOpenChange, cfg = {}, onComplete }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (!open) { setFile(null); setPreview(null); setDecisions({}); } }, [open]);

  const template = async () => {
    try {
      const res = await api.get("/users/import-template", { ...cfg, responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = u; a.download = "users_template.csv"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Could not download template"); }
  };

  const onPick = async (f) => {
    if (!f) return;
    setFile(f); setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/users/import", fd, { ...cfg, params: { commit: false }, headers: { ...(cfg.headers || {}), "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      const d = {}; (r.data.duplicates || []).forEach((x) => { d[x.email] = "skip"; }); setDecisions(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/users/import", fd, { ...cfg, params: { commit: true, decisions: JSON.stringify(decisions) }, headers: { ...(cfg.headers || {}), "Content-Type": "multipart/form-data" } });
      const s = r.data;
      toast.success(`${s.created} created · ${s.updated} updated · ${s.skipped} skipped`);
      if (s.errors?.length) toast.error(`${s.errors.length} row(s) had errors — see list`);
      onComplete?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const c = preview?.counts || {};
  const willCreate = (preview?.counts?.new || 0) + (preview?.duplicates || []).filter((x) => decisions[x.email] === "add").length;
  const willReplace = (preview?.duplicates || []).filter((x) => decisions[x.email] === "replace").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto kqm-scroll">
        <DialogHeader>
          <DialogTitle>Bulk upload user logins</DialogTitle>
          <DialogDescription>Upload a CSV of staff and admin logins. You'll review any duplicates before anything is saved.</DialogDescription>
        </DialogHeader>

        {!preview && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-500">Upload a CSV with columns <span className="font-medium text-slate-700">Password, First Name, Surname, Email</span> and an optional <span className="font-medium text-slate-700">Role</span> (staff or admin). Staff rows also create an employee profile. You'll get a review screen before anything is saved.</p>
            <div className="flex gap-2">
              <Button data-testid="uu-template" variant="outline" onClick={template}><Download className="h-4 w-4 mr-1" />Template</Button>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="uu-file" onChange={(e) => onPick(e.target.files?.[0])} />
              <Button data-testid="uu-choose" className="bg-slate-900 text-white" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Choose file</>}</Button>
            </div>
          </div>
        )}

        {preview && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 text-xs" data-testid="uu-summary">
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-semibold">{c.new || 0} new</span>
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">{c.duplicates || 0} possible duplicates</span>
              {(c.errors || 0) > 0 && <span className="px-2 py-1 rounded bg-rose-100 text-rose-700 font-semibold">{c.errors} error rows</span>}
              <span className="px-2 py-1 rounded bg-zinc-100 text-zinc-600">{c.total || 0} rows total</span>
            </div>

            {(preview.duplicates || []).length > 0 && (
              <div className="rounded-md border border-amber-200">
                <div className="px-3 py-2 bg-amber-50 text-amber-800 text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" />Possible duplicates — choose what to do</div>
                <div className="divide-y divide-zinc-100">
                  {preview.duplicates.map((x) => (
                    <div key={x.email} data-testid={`uu-dup-${x.email}`} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
                      <div className="text-sm min-w-0">
                        <div className="font-medium text-slate-900 truncate">{x.name} <span className="text-zinc-400 font-normal">· {x.email}</span></div>
                        <div className="text-xs text-zinc-500">{x.match_type === "exact" ? "An account with this email already exists" : `Same name as existing staff: ${x.existing?.name || ""}`}</div>
                      </div>
                      <div className="flex gap-3 text-xs shrink-0">
                        {(x.match_type === "exact" ? ["skip", "replace"] : ["skip", "add"]).map((opt) => (
                          <label key={opt} className="flex items-center gap-1 cursor-pointer capitalize">
                            <input type="radio" data-testid={`uu-dec-${x.email}-${opt}`} name={`dec-${x.email}`} checked={(decisions[x.email] || "skip") === opt} onChange={() => setDecisions((d) => ({ ...d, [x.email]: opt }))} />
                            {opt === "add" ? "Add anyway" : opt}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(preview.errors || []).length > 0 && (
              <ul className="text-xs text-rose-600 list-disc pl-5 max-h-32 overflow-y-auto" data-testid="uu-errors">
                {preview.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}

            <div className="flex items-center justify-between gap-3 border-t border-zinc-100 pt-3">
              <span className="text-sm text-zinc-500">Will create <span className="font-semibold text-slate-900">{willCreate}</span> · replace <span className="font-semibold text-slate-900">{willReplace}</span></span>
              <div className="flex gap-2">
                <Button variant="outline" data-testid="uu-cancel" onClick={() => { setPreview(null); setFile(null); }}>Back</Button>
                <Button data-testid="uu-confirm" className="bg-slate-900 text-white" disabled={busy} onClick={commit}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & create"}</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
